import { API_ENDPOINTS } from '../config/api';
import { fetchJson, fetchMultipart, fetchNoContent } from '../utils/api';
import type {
  AllowedSourcesResponse,
  AuditEventDto,
  BillingProviderSummary,
  BillerDetail,
  BillerSummary,
  BillingSource,
  CreateBillerRequest,
  ClaimDetailDto,
  ClaimSummaryDto,
  ClaimsPageDto,
  DispatchRequest,
  DispatchResultDto,
  EobImagePageDto,
  EobImageViewUrlDto,
  JobDto,
  Page,
  PracticeClaimsFilters,
  PracticeSummaryDto,
  PracticeSummary,
  PracticeDetail,
  Payer,
  ProviderSummary,
  UploadResultDto,
  UploadSummaryDto,
} from '../types/billing';

export const billingApi = {
  listUploads: (practiceId: string) =>
    fetchJson<UploadSummaryDto[]>(`${API_ENDPOINTS.billing.uploads()}?practiceId=${practiceId}`),

  uploadCsv: (practiceId: string, file: File, source: BillingSource = 'elation') => {
    const form = new FormData();
    form.append('practiceId', practiceId);
    form.append('source', source);
    form.append('file', file);
    return fetchMultipart<UploadResultDto>(API_ENDPOINTS.billing.uploads(), form);
  },

  listClaims: (uploadId: string) =>
    fetchJson<ClaimSummaryDto[]>(API_ENDPOINTS.billing.uploadClaims(uploadId)),

  listPracticeClaims: (practiceId: string, filters: PracticeClaimsFilters) => {
    const params = new URLSearchParams();
    if (filters.status?.length) params.set('status', filters.status.join(','));
    if (filters.uploadId) params.set('uploadId', filters.uploadId);
    if (filters.source) params.set('source', filters.source);
    if (filters.from) params.set('from', filters.from);
    if (filters.to) params.set('to', filters.to);
    if (filters.dir) params.set('dir', filters.dir);
    if (filters.cursor) params.set('cursor', filters.cursor);
    if (filters.limit !== undefined) params.set('limit', String(filters.limit));
    const qs = params.toString();
    const url = qs.length === 0
      ? API_ENDPOINTS.billing.practiceClaims(practiceId)
      : `${API_ENDPOINTS.billing.practiceClaims(practiceId)}?${qs}`;
    return fetchJson<ClaimsPageDto>(url);
  },

  getClaim: (claimId: string) =>
    fetchJson<ClaimDetailDto>(API_ENDPOINTS.billing.claim(claimId)),

  dispatch: (req: DispatchRequest) =>
    fetchJson<DispatchResultDto>(API_ENDPOINTS.billing.jobs(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(req),
    }),

  /** Non-archived providers for one practice - populates the "Map provider"
   *  dropdown so the operator picks the provider explicitly instead of relying
   *  on a "LAST, FIRST" parse of the claim's provider name. */
  listPracticeProviders: (practiceId: string) =>
    fetchJson<BillingProviderSummary[]>(API_ENDPOINTS.billing.practiceProviders(practiceId)),

  /** Manually map a Practice-Mate provider to its Office Ally ProviderID.
   *  When providerId is supplied the backend selects that provider directly and
   *  skips the "LAST, FIRST" name parse. providerName stays the exact claim name
   *  (the crosswalk key). Backend answers 204 on success, 422 when no provider
   *  matches, 400 on blank/non-numeric input - the thrown ApiError carries the body. */
  mapProvider: (
    practiceId: string,
    providerName: string,
    oaProviderId: string,
    providerId?: string,
  ) =>
    fetchNoContent(API_ENDPOINTS.billing.providerCrosswalk(practiceId), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ providerName, oaProviderId, providerId }),
    }),

  markComplete: (claimId: string, practiceId: string, note?: string) =>
    fetchJson<ClaimDetailDto>(API_ENDPOINTS.billing.markCompleted(claimId), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ practiceId, note: note?.trim() ? note.trim() : undefined }),
    }),

  unmarkComplete: (claimId: string, practiceId: string) =>
    fetchJson<ClaimDetailDto>(API_ENDPOINTS.billing.unmarkCompleted(claimId), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ practiceId }),
    }),

  listJobs: (practiceId: string, status?: string) => {
    const params = new URLSearchParams({ practiceId });
    if (status) params.set('status', status);
    return fetchJson<JobDto[]>(`${API_ENDPOINTS.billing.jobs()}?${params}`);
  },

  listAudit: (practiceId: string, page: number, size: number) => {
    const params = new URLSearchParams({ practiceId, page: String(page), size: String(size) });
    return fetchJson<Page<AuditEventDto>>(`${API_ENDPOINTS.billing.auditEvents()}?${params}`);
  },

  myPractices: () =>
    fetchJson<PracticeSummaryDto[]>(API_ENDPOINTS.billing.myPractices()),

  listClaimAudit: (claimId: string) =>
    fetchJson<AuditEventDto[]>(API_ENDPOINTS.billing.claimAudit(claimId)),

  getJobScreenshotUrl: (jobId: string) =>
    fetchJson<{ url: string }>(API_ENDPOINTS.billing.jobScreenshot(jobId)),

  /** GET /api/me/allowed-sources - returns the operator's department-derived
   *  source filter. Empty array = no filter (admin / unprovisioned). */
  getAllowedSources: () =>
    fetchJson<AllowedSourcesResponse>(API_ENDPOINTS.me.allowedSources()),

  listEobImages: (cursor: string | null, limit: number, q?: string) => {
    const params = new URLSearchParams({ limit: String(limit) });
    if (cursor) params.set('cursor', cursor);
    if (q && q.trim().length > 0) params.set('q', q.trim());
    return fetchJson<EobImagePageDto>(`${API_ENDPOINTS.billing.eobs()}?${params}`);
  },

  getEobImageViewUrl: (imageId: string) =>
    fetchJson<EobImageViewUrlDto>(API_ENDPOINTS.billing.eobViewUrl(imageId)),

  // ---- Biller management (ADMIN / CREDENTIALING_ADMIN) ----

  listBillers: () =>
    fetchJson<BillerSummary[]>(API_ENDPOINTS.billing.billers()),

  createBiller: (req: CreateBillerRequest) =>
    fetchJson<BillerSummary>(API_ENDPOINTS.billing.billers(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(req),
    }),

  getBiller: (id: string) =>
    fetchJson<BillerDetail>(API_ENDPOINTS.billing.biller(id)),

  grantPracticeToBiller: (billerId: string, practiceId: string) =>
    fetchJson<void>(API_ENDPOINTS.billing.billerPractices(billerId), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ practiceId }),
    }),

  revokePracticeFromBiller: (billerId: string, practiceId: string) =>
    fetchJson<void>(API_ENDPOINTS.billing.billerPractice(billerId, practiceId), {
      method: 'DELETE',
    }),

  // ---- Practices (billing-app practice management, old practices table) ----

  listPractices: () =>
    fetchJson<PracticeSummary[]>(API_ENDPOINTS.billing.practices()),

  getPractice: (id: string) =>
    fetchJson<PracticeDetail>(API_ENDPOINTS.billing.practice(id)),

  listPayers: () =>
    fetchJson<Payer[]>(API_ENDPOINTS.billing.payers()),

  // ---- Providers index (old providers table, enriched with billers) ----

  listProviders: () =>
    fetchJson<ProviderSummary[]>(API_ENDPOINTS.billing.providers()),

  // ---- Practice-side biller assignment (assign billers from a practice) ----

  listBillersOnPractice: (practiceId: string) =>
    fetchJson<BillerSummary[]>(API_ENDPOINTS.billing.practiceBillers(practiceId)),

  grantBillerToPractice: (practiceId: string, userId: string) =>
    fetchJson<void>(API_ENDPOINTS.billing.practiceBillers(practiceId), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId }),
    }),

  revokeBillerFromPractice: (practiceId: string, userId: string) =>
    fetchJson<void>(API_ENDPOINTS.billing.practiceBiller(practiceId, userId), {
      method: 'DELETE',
    }),
};
